<template>
    <Head>
        <title>Управление сетями</title>
    </Head>

    <authenticated-layout :notifications="notifications">
        <template #header>
            <NavigationSettings />
        </template>

        <div class="flex flex-col gap-8 p-8">
            <div class="flex flex-row items-baseline justify-between">
                <h1 class="text-2xl font-bold">Управление сетями</h1>
                <div class="flex flex-row gap-2">
                    <v-button color="green" @click="openNetworkModal(null)">Создать</v-button>
                </div>
            </div>

            <div class="flex flex-col bg-white rounded p-8">
                <div class="flex flex-row gap-8 mb-4 items-center">
                    <v-toggle label-left="MainNet" label="TestNet" v-model="testnet"/>
                </div>
                <div class="grid grid-cols-8 font-bold text-gray-700 p-2 gap-2 items-end">
                    <span>Название</span>
                    <span>Псевдоним</span>
                    <span>Активна</span>
                    <span>Поддержка переводов</span>
                    <span>Подтверждения</span>
                    <span>Мин. Время<br/> Заморозки</span>
                    <span>Макс. Время<br/> Заморозки</span>
                </div>

                <div>
                    <div
                        v-for="network in networks[testnet ? 'testnet' : 'mainnet'].slice().sort((a, b) => a.name > b.name ? 1 : -1)"
                        :key="network.id"
                        class="grid grid-cols-8 p-2 gap-2 text-sm border-t hover:bg-gray-100 items-center"
                    >
                        <span>{{ network.name }}</span>
                        <span>{{ network.alias }}</span>
                        <span :class="network.isActive ? 'font-bold' : 'text-gray-500'">{{ network.isActive }}</span>
                        <span :class="network.pendingWithdrawals ? 'font-bold' : 'text-gray-500'">{{ network.pendingWithdrawals }}</span>
                        <span>{{ network.confirmations }}</span>
                        <span>{{ network.minFreezeTime }}</span>
                        <span>{{ network.maxFreezeTime }}</span>
                        <div class="flex flex-row gap-2 items-start justify-end">
                            <v-button color="red" @click="deleteNetwork(network)" type="button">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 fill-current" viewBox="0 0 16 16">
                                    <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                                </svg>
                            </v-button>
                            <v-button color="green" @click="openNetworkModal(network)" type="button">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 fill-current" viewBox="0 0 16 16">
                                    <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z"/>
                                </svg>
                            </v-button>
                        </div>
                    </div>
                </div>
            </div>

            <v-modal :showing="showNetworkModal" @close="showNetworkModal = false">
                <template v-slot:header>
                    {{ modalNetwork.id ? `Редактирование сети: ${modalNetwork.name}` : 'Создание сети' }}
                </template>
                <div class="grid grid-cols-2 gap-4">
                    <v-input label="Название" v-model="modalNetwork.name" />
                    <v-input label="Псевдоним" v-model="modalNetwork.alias" />
                    <v-toggle label="Активна" v-model="modalNetwork.isActive" />
                    <v-toggle label="Ожидание вывода" v-model="modalNetwork.pendingWithdrawals" />
                    <v-toggle label="Монета со сдачей" v-model="modalNetwork.hasChange" />
                    <v-toggle label="На техническом обслуживании" v-model="modalNetwork.underMaintenance" />
                    <v-input label="Тип блокчейна (eth-, btc- и т.п.)" v-model="modalNetwork.blockchainType" />
                    <v-input label="Имя сети на бирже" v-model="modalNetwork.exchangeName" />
                    <v-input label="Псевдоним в системе KYT" v-model="modalNetwork.kytAlias" />
                    <v-input label="Количество подтверждений для сети" type="number" min="0" v-model="modalNetwork.confirmations" />
                    <v-input label="Мин. Время Заморозки (мин)" type="number" min="0" v-model="modalNetwork.minFreezeTime" />
                    <v-input label="Макс. Время Заморозки (мин)" type="number" min="0" v-model="modalNetwork.maxFreezeTime" />
                    <v-input label="Регулярное выражение адреса" class="col-span-full" v-model="modalNetwork.addressRegex" />
                    <v-input label="Регулярное выражение тега" class="col-span-full" v-model="modalNetwork.tagRegex" />
                    <v-input label="Ссылка на транзакцию" class="col-span-full" v-model="modalNetwork.txLink" />
                    <v-input label="Ссылка на адрес" class="col-span-full" v-model="modalNetwork.addressLink" />
                    <v-input label="Ссылка на контракт" class="col-span-full" v-model="modalNetwork.contractLink" />
                </div>
                <template v-slot:footer>
                    <v-button type="button" color="green" :disabled="loading" @click="saveNetwork">Сохранить</v-button>
                </template>
            </v-modal>
        </div>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from '@/Layouts/Authenticated.vue';
import NavigationSettings from '@/Components/Navigation/Administration';
import { Head } from '@inertiajs/inertia-vue3';
import VButton from '@/Components/Button';
import VInput from '@/Components/Input';
import VToggle from '@/Components/Toggle';
import VModal from '@/Components/Modal';
import VSelect from "@/Components/Select.vue";

export default {
    components: {
        VSelect,
        Head,
        NavigationSettings,
        AuthenticatedLayout,
        VButton,
        VInput,
        VToggle,
        VModal,
    },
    data() {
        return {
            testnet: false,
            loading: false,
            showNetworkModal: false,
            modalNetwork: {},
        };
    },
    props: {
        networks: Object,
        notifications: Array,
    },
    watch: {
        testnet(newValue) {
            window.location.hash = newValue ? '#testnet' : '#mainnet';
        },
    },
    mounted() {
        const hash = window.location.hash.replace('#', '');

        this.testnet = hash === 'testnet';
    },
    methods: {
        openNetworkModal(network = null) {
            this.showNetworkModal = true;

            this.modalNetwork = network
                ? JSON.parse(JSON.stringify(network))
                : {
                    name: '',
                    exchangeName: '',
                    alias: '',
                    addressRegex: '',
                    isActive: false,
                    confirmations: 12,
                    tagRegex: '',
                    pendingWithdrawals: false,
                    underMaintenance: false,
                    minFreezeTime: 0,
                    maxFreezeTime: 0,
                    kytAlias: '',
                    txLink: '',
                    addressLink: '',
                    contractLink: '',
                    blockchainType: '',
                    hasChange: false,
                };
        },
        saveNetwork() {
            this.loading = true;
            const network = this.modalNetwork;
            network.testnet = this.testnet;

            axios.post(network.id
                    ? this.route('admin.network.update')
                    : this.route('admin.network.create')
                , network)
                .then(res => {
                    this.notifications.push(...res.data.notifications);
                    this.loading = false;

                    if (res.data.success) {
                        const updatedNetwork = res.data.result;
                        const index = this.networks[this.testnet ? 'testnet' : 'mainnet'].findIndex(n => n.id === updatedNetwork.id);

                        if (index !== -1) {
                            this.networks[this.testnet ? 'testnet' : 'mainnet'].splice(index, 1, updatedNetwork);
                        } else {
                            this.networks[this.testnet ? 'testnet' : 'mainnet'].push(updatedNetwork);
                        }

                        this.showNetworkModal = false;
                    }
                })
                .catch(err => {
                    console.log(err)
                    this.loading = false;
                    this.notifications.push({
                        type: 'error',
                        title: `Ошибка`,
                        message: err.response?.data.message || 'Что-то пошло не так',
                    });
                });
        },
        deleteNetwork(network) {
            if (!window.confirm(`Удалить сеть ${network.name}?`)) return;

            axios.post(this.route('admin.network.delete'), { id: network.id, testnet: this.testnet })
                .then(res => {
                    this.notifications.push(...res.data.notifications);

                    if (res.data.success) {
                        this.networks[this.testnet ? 'testnet' : 'mainnet'] = this.networks[this.testnet ? 'testnet' : 'mainnet'].filter(n => n.id !== network.id);
                    }
                })
                .catch(err => {
                    this.notifications.push({
                        type: 'error',
                        title: `Ошибка`,
                        message: err.response?.data.message || 'Что-то пошло не так',
                    });
                });
        },
    },
};
</script>
